import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IdentityA11yAnnouncer,
  IdentityButton,
  IdentityFooterInline,
  IdentityHero,
  IdentityLayout,
  IdentityLoading,
  IdentitySuccess,
  IdentityVerificationBanner,
  PasswordStrengthMeter,
  IDENTITY_BUTTON_LABELS,
  CANONICAL_SUCCESS_MESSAGES,
  evaluatePasswordStrength,
  IdentityError as IdentityErrorView,
} from "@speedycore/identity-ui";
import {
  identityChangeEmail,
  identityChangePassword,
  identityDeleteAccount,
  identityGetSessions,
  identityLogoutAllSessions,
  identitySessionHandoff,
  LoginSuccess,
  sendIdentityTelemetry,
} from "../lib/identityApi";
import { useSilentRefresh } from "../hooks/useSilentRefresh";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function IdentitySettingsPage() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState<LoginSuccess | null>(null);
  const [loadBusy, setLoadBusy] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [a11y, setA11y] = useState("");
  const [a11yType, setA11yType] = useState<"status" | "error">("status");

  useEffect(() => {
    let cancelled = false;
    setLoadBusy(true);
    void identitySessionHandoff("identity_web")
      .then((tokens) => {
        if (cancelled) return;
        if (!tokens) {
          setLoadError("Please sign in to access SpeedyCore Identity Settings.");
          window.setTimeout(() => navigate("/login"), 1500);
          return;
        }
        setAuth(tokens);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError("Please sign in to access SpeedyCore Identity Settings.");
          window.setTimeout(() => navigate("/login"), 1500);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useSilentRefresh({
    accessToken: auth?.access_token,
    refreshToken: auth?.refresh_token ?? null,
    onTokens: (next) => {
      setAuth((prev) => (prev ? { ...prev, ...next } : next));
    },
    onExpired: () => {
      void sendIdentityTelemetry("identity.token_expired");
      navigate("/login?expired=1");
    },
    enabled: Boolean(auth),
  });

  return (
    <>
      <IdentityA11yAnnouncer message={a11y} type={a11yType} />
      <IdentityLayout
        hero={
          <IdentityHero
            title="SpeedyCore Identity Settings"
            subtitle="Manage your account, security, and connected devices."
          />
        }
        form={
          loadBusy ? (
            <IdentityLoading message="Loading SpeedyCore Identity settings…" />
          ) : loadError && !auth ? (
            <>
              <IdentityErrorView message={loadError} />
              <IdentityFooterInline />
            </>
          ) : auth ? (
            <SettingsBody
              auth={auth}
              onA11y={(msg, type) => {
                setA11y(msg);
                setA11yType(type ?? "status");
              }}
              onTokens={(next) => setAuth((prev) => (prev ? { ...prev, ...next } : next))}
              onSignOut={() => navigate("/login?logged_out=1")}
            />
          ) : null
        }
      />
    </>
  );
}

function SettingsBody(props: {
  auth: LoginSuccess;
  onA11y: (msg: string, type?: "status" | "error") => void;
  onTokens: (next: LoginSuccess) => void;
  onSignOut: () => void;
}) {
  const { auth, onA11y, onSignOut } = props;

  return (
    <div className="sc-identity-settings" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <AccountSection auth={auth} onA11y={onA11y} />
      <SecuritySection auth={auth} onA11y={onA11y} onSignOut={onSignOut} />
      <DangerZone auth={auth} onA11y={onA11y} onSignOut={onSignOut} />
      <div className="sc-identity-links" style={{ marginTop: "0.5rem" }}>
        <Link to="/auth/login">Back to sign in</Link>
      </div>
      <IdentityFooterInline />
    </div>
  );
}

function AccountSection(props: {
  auth: LoginSuccess;
  onA11y: (msg: string, type?: "status" | "error") => void;
}) {
  const { auth, onA11y } = props;
  const token = auth.access_token;
  const userEmail = auth.user?.email ?? "";

  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailDone, setEmailDone] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  const [showPw, setShowPw] = useState(false);

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    setEmailBusy(true);
    setEmailError(null);
    setEmailDone(false);
    try {
      await identityChangeEmail(token, newEmail.trim().toLowerCase());
      setEmailDone(true);
      setNewEmail("");
      onA11y(CANONICAL_SUCCESS_MESSAGES.emailChanged);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not change email.";
      setEmailError(msg);
      onA11y(msg, "error");
      void sendIdentityTelemetry("identity.permission_denied", { error_code: "change_email_failed" });
    } finally {
      setEmailBusy(false);
    }
  }

  async function submitPw(e: FormEvent) {
    e.preventDefault();
    setPwBusy(true);
    setPwError(null);
    setPwDone(false);
    const pw = newPw;
    if (pw.length < 8) {
      setPwError("Password too short");
      onA11y("Password too short", "error");
      setPwBusy(false);
      return;
    }
    if (pw !== confirmPw) {
      setPwError("Passwords do not match");
      onA11y("Passwords do not match", "error");
      setPwBusy(false);
      return;
    }
    try {
      await identityChangePassword(token, curPw, pw);
      setPwDone(true);
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
      onA11y(CANONICAL_SUCCESS_MESSAGES.passwordChanged);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not change password.";
      setPwError(msg);
      onA11y(msg, "error");
      void sendIdentityTelemetry("identity.permission_denied", { error_code: "change_password_failed" });
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <section className="sc-identity-card" aria-labelledby="account-title">
      <h2 id="account-title" className="sc-identity-card-title">Account</h2>

      <h3 style={{ marginTop: "0.75rem", fontSize: "1rem", fontWeight: 600 }}>Change email</h3>
      <p className="sc-identity-field-hint" style={{ marginBottom: "0.75rem", color: "#5a607a", fontSize: "0.88rem" }}>
        Current email: <strong>{userEmail || "—"}</strong>
      </p>
      <form className="sc-identity-form" onSubmit={(e) => void submitEmail(e)} noValidate style={{ marginBottom: "1.5rem" }}>
        <label>
          <span className="sc-identity-field-label">New email</span>
          <input
            type="email"
            inputMode="email"
            name="new-email"
            id="settings-new-email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            placeholder="you@example.com"
          />
        </label>
        {emailDone ? (
          <IdentitySuccess message="emailChanged" />
        ) : emailError ? (
            <IdentityErrorView message={emailError} />
          ) : null}
        <IdentityButton
          type="submit"
          label={IDENTITY_BUTTON_LABELS.changeEmail}
          loading={emailBusy}
          variant="secondary"
        />
      </form>

      <h3 style={{ fontSize: "1rem", fontWeight: 600 }}>Change password</h3>
      <p className="sc-identity-field-hint" style={{ marginBottom: "0.75rem", color: "#5a607a", fontSize: "0.88rem" }}>
        Use at least 8 characters with a mix of letters, numbers, and symbols.
      </p>
      <form className="sc-identity-form" onSubmit={(e) => void submitPw(e)} noValidate>
        <label>
          <span className="sc-identity-field-label">Current password</span>
          <input
            type={showPw ? "text" : "password"}
            id="settings-current-pw"
            name="current_password"
            autoComplete="current-password"
            value={curPw}
            onChange={(e) => setCurPw(e.target.value)}
            required
            minLength={8}
          />
        </label>
        <label>
          <span className="sc-identity-field-label">New password</span>
          <div className="sc-identity-row">
            <input
              type={showPw ? "text" : "password"}
              id="settings-new-pw"
              name="new_password"
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
              aria-describedby="settings-pw-strength"
            />
            <button
              type="button"
              className="sc-identity-link-btn"
              aria-pressed={showPw}
              aria-controls="settings-new-pw"
              onClick={() => setShowPw((s) => !s)}
            >
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <PasswordStrengthMeter password={newPw} showLabel showBreachCheckMicrocopy />
        <label>
          <span className="sc-identity-field-label">Confirm new password</span>
          <input
            type={showPw ? "text" : "password"}
            id="settings-confirm-pw"
            name="confirm_password"
            autoComplete="new-password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={8}
          />
        </label>
        {pwDone ? (
          <IdentitySuccess message="passwordChanged" />
        ) : pwError ? (
          <IdentityErrorView message={pwError} />
        ) : null}
        <IdentityButton
          type="submit"
          label={IDENTITY_BUTTON_LABELS.changePassword}
          loading={pwBusy}
          variant="secondary"
        />
      </form>
    </section>
  );
}

function SecuritySection(props: {
  auth: LoginSuccess;
  onA11y: (msg: string, type?: "status" | "error") => void;
  onSignOut: () => void;
}) {
  const { auth, onA11y, onSignOut } = props;
  const token = auth.access_token;
  const [sessions, setSessions] = useState<Array<{ id: string; ip_masked: string; device: string; created_at: string; expires_at: string; current?: boolean }>>([]);
  const [loadSessBusy, setLoadSessBusy] = useState(true);
  const [loadSessError, setLoadSessError] = useState<string | null>(null);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutDone, setLogoutDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadSessBusy(true);
    void identityGetSessions(token)
      .then((r) => {
      if (cancelled) return;
      setSessions(r.sessions);
      setLoadSessError(null);
    })
    .catch((err) => {
      if (!cancelled) {
        setLoadSessError(err instanceof Error ? err.message : "Could not load sessions.");
      }
    })
    .finally(() => {
      if (!cancelled) setLoadSessBusy(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleLogoutAll() {
    setLogoutBusy(true);
    try {
      await identityLogoutAllSessions(token);
      setLogoutDone(true);
      onA11y("All other sessions signed out.");
      void sendIdentityTelemetry("identity.login_failed", { error_code: "logout_all" });
      window.setTimeout(() => onSignOut(), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not sign out all sessions.";
      onA11y(msg, "error");
      setLogoutBusy(false);
    }
  }

  return (
    <section className="sc-identity-card" aria-labelledby="security-title">
      <h2 id="security-title" className="sc-identity-card-title">Security</h2>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: "0.5rem", marginBottom: "0.75rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>Active sessions</h3>
        <IdentityButton
          label={IDENTITY_BUTTON_LABELS.logoutAllSessions}
          loading={logoutBusy}
          variant="secondary"
          onClick={handleLogoutAll}
        />
      </div>
      {logoutDone ? (
        <IdentitySuccess message="All other sessions have been signed out. Redirecting to sign in…" />
      ) : null}
      {loadSessBusy ? (
        <IdentityLoading message="Loading active sessions…" />
      ) : loadSessError ? (
          <IdentityErrorView message={loadSessError} />
        ) : sessions.length === 0 ? (
          <p className="sc-identity-field-hint" style={{ color: "#5a607a" }}>No active sessions found.</p>
        ) : (
          <div className="sc-identity-settings__sessions">
            {sessions.map((s) => (
              <div key={s.id} className="sc-identity-settings__session">
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {s.device || "Unknown device"}
                      {s.current ? <span style={{ color: "var(--sc-identity-success)", marginLeft: "0.5rem", fontSize: "0.8rem" }}>Current</span> : null}
                    </div>
                    <div className="sc-identity-field-hint" style={{ fontSize: "0.8rem", color: "#5a607a" }}>
                      {s.ip_masked || "IP masked"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.8rem", color: "#5a607a" }}>Signed in {formatDate(s.created_at)}</div>
                    <div style={{ fontSize: "0.8rem", color: "#5a607a" }}>Expires {formatDate(s.expires_at)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </section>
  );
}

function DangerZone(props: {
  auth: LoginSuccess;
  onA11y: (msg: string, type?: "status" | "error") => void;
  onSignOut: () => void;
}) {
  const { auth, onA11y, onSignOut } = props;
  const token = auth.access_token;
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await identityDeleteAccount(token);
      onA11y("Account scheduled for deletion.");
      void sendIdentityTelemetry("identity.permission_denied", { error_code: "account_deleted" });
      window.setTimeout(() => onSignOut(), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not delete account.";
      setError(msg);
      onA11y(msg, "error");
      setBusy(false);
    }
  }

  return (
    <section className="sc-identity-card sc-identity-settings__danger" aria-labelledby="danger-title">
      <h2 id="danger-title" className="sc-identity-card-title" style={{ color: "var(--sc-identity-error)" }}>Danger Zone</h2>
      <p className="sc-identity-field-hint" style={{ marginTop: "0.75rem", marginBottom: "0.75rem" }}>
        <strong>Delete your SpeedyCore Identity</strong> and all connected data will be signed out immediately. Your data will remain recoverable for 30 days.
      </p>
      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.75rem 0 1rem", cursor: "pointer" }}>
        <input
          type="checkbox"
          id="danger-confirm"
          name="danger_confirm"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          aria-describedby="danger-hint"
        />
        <span htmlFor="danger-confirm">I understand this cannot be undone within the 30-day recovery window.</span>
      </label>
      {error ? <IdentityErrorView message={error} /> : null}
      <IdentityButton
        label={IDENTITY_BUTTON_LABELS.deleteAccount}
        loading={busy}
        variant="secondary"
        disabled={!confirmed}
        onClick={handleDelete}
      />
    </section>
  );
}
